import { useState } from 'react';
import { Form, Button, Input, InputNumber, Checkbox, FormInstance, Select, ColProps } from 'antd';
import 'moment/locale/fa';
import { DatePicker as DatePickerJalali } from "antd-jalali";
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import { enmControlType, IProperty } from './interfaces';
import { IEntity } from '../infrastructure/models';
import { IBaseComponent, IBaseStates } from '../infrastructure/baseComponent';
import Password from 'antd/lib/input/Password';
import dayjs from 'dayjs';
import { Lookup } from '../lookup';
import CustomSpinner from '../infrastructure/customSpinner';
import React from 'react';
import { PriceInput } from '../priceInput';

const { Option } = Select;

type LayoutType = Parameters<typeof Form>[0]['layout'];

export interface IDataFormProps<TEntity extends IEntity> {
    borderLess?: boolean;
    onSubmit: (values: any) => Promise<boolean>;
    onReset?: () => void;
    isReadonly?: boolean;
    labelCol?: ColProps;
    wrapperCol?: ColProps;
    properties: IProperty[];
    currentEntity: TEntity;
    onChange?: (property: IProperty, value: any, form: FormInstance<any>) => void;
    resetable?: boolean;
    style?: React.CSSProperties;
    parent: IBaseComponent;
    preparelookupProps?: (propName: string, lookupProps: any, form?: FormInstance<any>) => any;
    onLookupOpen?: (propName: string) => void;
    onLookupClose?: (propName: string, selectetEntity?: any) => void;
}

const DataForm = <TEntity extends IEntity>(props: IDataFormProps<TEntity>) => {
    const [state, setState] = useState<IBaseStates>({ loading: false, isAuthorized: true, windowSize: window.innerWidth <= 765 ? 1 : 2 });
    const { loading } = state;
    const [form] = Form.useForm();
    const [formLayout] = useState<LayoutType>('horizontal');
    const formItemLayout = {
        labelCol: props.labelCol ?? { xxl: 4, xl: 4, lg: 6, md: 6, sm: 8, xs: 8 },
        wrapperCol: props.wrapperCol ?? { xxl: 10, xl: 10, lg: 16, md: 16, sm: 16, xs: 16 },
    };

    const componentWillUnmount = React.useRef(false);

    const onFinish = async (values: any) => {
        setState({ loading: true, isAuthorized: true, windowSize: window.innerWidth <= 765 ? 1 : 2 });
        await props.onSubmit(values);
        if (componentWillUnmount.current !== true)
            setState({ loading: false, isAuthorized: true, windowSize: window.innerWidth <= 765 ? 1 : 2 });
    };

    React.useEffect(() => {
        return () => {
            componentWillUnmount.current = true;
        };
    }, []);


    let extraParams = {};

    const formNode = (
        <Form
            style={{ padding: props.borderLess ? 0 : '5px 25px' }}
            scrollToFirstError
            {...formItemLayout}
            layout={formLayout}
            form={form}
            initialValues={{ layout: formLayout }}
            onFinish={onFinish}
            onReset={() => {
                setState({ loading: true, isAuthorized: true, windowSize: window.innerWidth <= 765 ? 1 : 2 });
                props.onReset && props.onReset();
            }}
        >

            {!props.currentEntity ? <CustomSpinner /> :
                <div style={{ marginBottom: 10, position: 'sticky' }}>
                    {props.properties.filter(c => c.showControl !== false).map(c => {

                        let disabled = props.isReadonly || c.disabled || c.disable?.call(c, props.parent);

                        let currentValue = (props.currentEntity as any);
                        let fields = c.dataIndex!.split('.');

                        fields.forEach(i => {
                            if (currentValue !== undefined && currentValue != null && currentValue[i] !== undefined && currentValue[i] != null)
                                currentValue = currentValue[i];
                            else
                                currentValue = undefined;
                        });

                        let element: React.ReactNode | null = null;
                        if (c.controlType === enmControlType.textbox) {
                            element = (
                                <Input
                                    onChange={e => {
                                        e.stopPropagation();
                                        if (props.onChange)
                                            props.onChange(c, e.target.value, form);
                                    }}
                                    placeholder={c.title}
                                    style={{ width: '100%', minWidth: c.width, marginBottom: 8, display: 'block' }}
                                    type={"text"}
                                    key={c.dataIndex}
                                    disabled={disabled}
                                />);

                            let fields = c.dataIndex?.split('.');
                            let value = props.currentEntity as any;
                            fields?.forEach(i => {
                                if (value && value[i])
                                    value = value[i];
                                else
                                    value = undefined;
                            });
                            extraParams = { initialValue: value ?? currentValue };
                        }
                        if (c.controlType === enmControlType.password) {
                            element = (
                                <Password
                                    onChange={e => {
                                        e.stopPropagation();
                                        if (props.onChange)
                                            props.onChange(c, e.target.value, form);
                                    }}
                                    placeholder={c.title}
                                    style={{ width: '100%', minWidth: c.width, marginBottom: 8, display: 'block' }}
                                    type={"password"}
                                    key={c.dataIndex}
                                    disabled={disabled}
                                />);

                            extraParams = { initialValue: undefined };
                        }
                        if (c.controlType === enmControlType.email) {
                            element = (
                                <Input
                                    onChange={e => {
                                        e.stopPropagation();
                                        if (props.onChange)
                                            props.onChange(c, e.target.value, form);
                                    }}
                                    placeholder={c.title}
                                    style={{ width: '100%', minWidth: c.width, marginBottom: 8, display: 'block' }}
                                    type={"email"}
                                    key={c.dataIndex}
                                    disabled={disabled}
                                    maxLength={c.maxLength}
                                    minLength={c.minLength}
                                />);

                            extraParams = { initialValue: currentValue };
                        }
                        if (c.controlType === enmControlType.template && c.renderControl) {
                            element = c.renderControl(props.currentEntity);
                            if (c.renderExtraParams)
                                extraParams = c.renderExtraParams(props.currentEntity);
                            else
                                extraParams = { initialValue: currentValue };
                        }
                        if (c.controlType === enmControlType.numberinput) {
                            element = (
                                <InputNumber
                                    onChange={value => {
                                        if (props.onChange)
                                            props.onChange(c, value, form);
                                    }}
                                    onKeyPress={e => c.onKeyPress && c.onKeyPress(e, form?.getFieldValue(c.dataIndex!))}
                                    placeholder={c.title}
                                    style={{ width: '100%', minWidth: c.width, marginBottom: 8, display: 'block' }}
                                    type={"text"}
                                    key={c.dataIndex}
                                    disabled={disabled}
                                    precision={c.precision}
                                    formatter={c.formatter}
                                    parser={c.parser}
                                    maxLength={c.maxLength}
                                    minLength={c.minLength}
                                />);

                            let fields = c.dataIndex?.split('.');
                            let value = props.currentEntity as any;
                            fields?.forEach(i => {
                                if (value && value[i])
                                    value = value[i];
                                else
                                    value = undefined;
                            });
                            extraParams = { initialValue: value ?? currentValue };
                        }
                        if (c.controlType === enmControlType.priceinput) {
                            element = (
                                <PriceInput
                                    onChange={value => {
                                        if (props.onChange)
                                            props.onChange(c, value, form);
                                    }}
                                    placeholder={c.title}
                                    style={{ width: '100%', minWidth: c.width, marginBottom: 8, display: 'block' }}

                                    key={c.dataIndex}
                                    disabled={disabled}
                                />);

                            let fields = c.dataIndex?.split('.');
                            let value = props.currentEntity as any;
                            fields?.forEach(i => {
                                if (value && value[i])
                                    value = value[i];
                                else
                                    value = undefined;
                            });
                            extraParams = { initialValue: value ?? currentValue };
                        }
                        if (c.controlType === enmControlType.lookup) {
                            element = (
                                <Lookup
                                    onChange={value => {
                                        if (value) {
                                            (props.currentEntity as any)[c.dataIndex!] = value["Id"];
                                            (props.currentEntity as any)[c.lookup?.lookupProperty!] = value;
                                            if (props.onChange)
                                                props.onChange(c, value, form);
                                        }
                                        else {
                                            (props.currentEntity as any)[c.dataIndex!] = undefined;
                                            (props.currentEntity as any)[c.lookup?.lookupProperty!] = undefined;
                                            if (props.onChange)
                                                props.onChange(c, undefined, form);
                                        }
                                    }}
                                    parent={props.parent}
                                    isLookupMode={true}
                                    property={c}
                                    key={c.dataIndex}
                                    disabled={disabled}
                                    preparelookupProps={lookupProps => {
                                        if (props.preparelookupProps)
                                            return props.preparelookupProps(c.dataIndex!, lookupProps, form);
                                        return lookupProps;
                                    }}
                                    onLookupOpen={() => props.onLookupOpen && props.onLookupOpen(c.dataIndex!)}
                                    onLookupClose={(selectedEntity) => props.onLookupClose && props.onLookupClose(c.dataIndex!, selectedEntity)}
                                />);

                            extraParams = { initialValue: (props.currentEntity as any)[c.lookup?.lookupProperty!] };
                        }
                        if (c.controlType === enmControlType.checkbox) {
                            element = (
                                <Checkbox
                                    onChange={e => {
                                        if (props.onChange)
                                            props.onChange(c, e.target.checked, form);
                                    }}
                                    key={c.dataIndex}
                                    disabled={disabled}
                                />);
                            extraParams = { valuePropName: "checked" };
                        }
                        if (c.controlType === enmControlType.combobox) {
                            let options: { value?: any; title?: string; }[] = [];

                            let fields = c.dataIndex?.split('.');
                            let value = props.currentEntity as any;
                            fields?.forEach(i => {
                                if (value && value[i])
                                    value = value[i];
                                else
                                    value = undefined;
                            });

                            if (c.combobox?.options)
                                options = c.combobox.options;
                            if (c.combobox?.optionsFunc)
                                options = c.combobox.optionsFunc(props.parent, props.currentEntity);

                            element = (
                                <Select
                                    onChange={value => {
                                        if (props.onChange)
                                            props.onChange(c, value, form);
                                    }}
                                    key={c.dataIndex}
                                    disabled={disabled}
                                    defaultValue={c.combobox?.defaultValue}
                                    mode={c.combobox?.mode}
                                >
                                    {options.map(o => <Option key={o.value} value={o.value}>{o.title}</Option>)}
                                </Select>);
                            extraParams = { initialValue: c.combobox?.defaultValue ?? value ?? currentValue };
                        }
                        if (c.controlType === enmControlType.date) {
                            element = (
                                <DatePickerJalali
                                    key={c.dataIndex}
                                    onChange={(e: any) => {
                                        if (props.onChange) {
                                            if (e) {
                                                if (e.locale() === "fa") {
                                                    const date = dayjs(e.toDate()).locale('fa').format('YYYY-MM-DD');
                                                    const jalali = dayjs(date, { jalali: true } as any);
                                                    const miladi = jalali.locale('en').format('YYYY-MM-DD');
                                                    let parsedDate = new Date(miladi);
                                                    parsedDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
                                                    props.onChange(c, parsedDate, form);
                                                }
                                                else {
                                                    let parsedDate = e.toDate();
                                                    parsedDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
                                                    props.onChange(c, parsedDate, form);
                                                }
                                            }
                                            else
                                                props.onChange(c, undefined, form);
                                        }
                                    }}
                                    placeholder={c.title}
                                    format="YYYY/MM/DD"
                                    style={{ width: '100%', minWidth: c.width, marginBottom: 8, display: 'block' }}
                                    disabled={disabled}
                                //inputReadOnly={true}
                                />);

                            let currentDate = currentValue;
                            if (currentDate && !dayjs.isDayjs(currentDate))
                                currentDate = dayjs(currentDate).calendar('jalali');
                            extraParams = { initialValue: currentDate };
                        }
                        if (c.controlType === enmControlType.datetime) {
                            element = (
                                <DatePickerJalali
                                    key={c.dataIndex}
                                    onChange={(e: any) => {
                                        if (props.onChange) {
                                            if (e) {
                                                if (e.locale() === "fa") {
                                                    const date = dayjs(e.toDate()).locale('fa').format('YYYY-MM-DD HH:mm');
                                                    const jalali = dayjs(date, { jalali: true } as any);
                                                    const miladi = jalali.locale('en').format('YYYY-MM-DD HH:mm');
                                                    let parsedDate = new Date(miladi);
                                                    parsedDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
                                                    props.onChange(c, parsedDate, form);
                                                }
                                                else {
                                                    let parsedDate = e.toDate();
                                                    parsedDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
                                                    props.onChange(c, parsedDate, form);
                                                }
                                            }
                                            else
                                                props.onChange(c, undefined, form);
                                        }
                                    }}
                                    placeholder={c.title}
                                    format="YYYY/MM/DD HH:mm"
                                    style={{ width: '100%', minWidth: c.width, marginBottom: 8, display: 'block' }}
                                    disabled={disabled}
                                    showTime={true}
                                //inputReadOnly={true}
                                />);
                            let currentDate = currentValue;
                            if (currentDate && !dayjs.isDayjs(currentDate))
                                currentDate = dayjs(currentDate).calendar('jalali');
                            extraParams = { initialValue: currentDate };
                        }
                        return (
                            <Form.Item
                                label={c.title}
                                name={c.dataIndex}
                                rules={c.rules ?? (c.rulesFunc && c.rulesFunc(props.currentEntity))}
                                key={c.dataIndex}
                                initialValue={currentValue}
                                style={{ display: !c.visibility || (c.visibility && c.visibility(props.currentEntity)) ? 'flex' : 'none', minHeight: 40 }}
                                {...extraParams}
                            >
                                {element}
                            </Form.Item>
                        );
                    })}
                </div>}

            {!props.isReadonly &&
                <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ zIndex: 1000, borderTop: '1px solid #e5e5e5', position: "sticky", bottom: -10, backgroundColor: props.style?.backgroundColor ?? '#F5F5F5' }}>

                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 10, marginBottom: 10 }}>
                        ذخیره
                    </Button>

                    <Button type="ghost" htmlType="reset" icon={<StopOutlined />} loading={loading} style={{ display: props.resetable !== false ? 'inline-block' : 'none', lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 10, marginBottom: 10, marginLeft: 10, background: '#fff' }}>
                        لغو
                    </Button>
                </Form.Item>
            }
        </Form>
    );

    if (props.borderLess)
        return <>{formNode}</>;

    return (
        <div className="box box-blue" style={{ margin: '0px 0px', ...props.style }}>
            <div className="box-body form">
                <div className="form-body">
                    {formNode}
                </div>
            </div>
        </div>
    );
};

export default DataForm;